import React, { memo, useLayoutEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import GroupTable from 'components/UiKit/GroupTable';
import groupByCrop from 'screens/Property/helpers/groupByCrops';
import useMassAssignerTools from 'screens/Property/hooks/useMassAssignerTools';
import AddCropZonesModal from '../AddCropZonesModal';
import MassAssignerActions from '../MassAssignerActions';
import ExportPDFMapsFooter from '../../../PanelFooter/components/ExportPDFMapsFooter';
import GroupByCrop from './components/GroupByCrop';
import GroupByFarm from './components/GroupByFarms';
import './index.css';

const massAssignerTreeDefaultPropProperties = [];
const massAssignerTreeDefaultPropFieldsAndAreasGeoJSONCollection = {};
const massAssignerTreeDefaultPropFieldsToMassiveAssign = [];
const MassAssignerTree = ({
  properties = massAssignerTreeDefaultPropProperties,
  fieldsAndAreasGeoJSONCollection = massAssignerTreeDefaultPropFieldsAndAreasGeoJSONCollection,
  propertiesFilter,
  fieldsToMassiveAssign = massAssignerTreeDefaultPropFieldsToMassiveAssign,
  searchTextValueFFT,
  isExportPDFMapsActive,
  isSortByCrop,
  dispatch
}) => {
  const geoJSONMap = useMemo(() => {
    const map = new Map();
    fieldsAndAreasGeoJSONCollection?.features?.forEach(f => {
      map.set(f.properties.id, f);
    });
    return map;
  }, [fieldsAndAreasGeoJSONCollection]);

  const isSearching = Boolean(searchTextValueFFT?.valueInput?.length);

  const processedProperties = useMemo(() => {
    const mapped = properties.map(property => ({
      ...property,
      fields: property.fields.map(field => ({
        ...field,
        farmId: property.id,
        farmName: property.name
      }))
    }));
    // Both groupings show (and select from) only the search results.
    const filtered = propertiesFilter(mapped, searchTextValueFFT);
    return isSortByCrop ? groupByCrop(filtered) : filtered;
  }, [properties, isSortByCrop, propertiesFilter, searchTextValueFFT]);

  const {
    handleSelectedFarm,
    handleSelectedField,
    isFarmSelected,
    isFieldSelected,
    isCropZoneSelected,
    isPropertyIndeterminate,
    handleSelectedCropZone,
    handleSelectAllProperties,
    allPropertiesSelected,
    allPropertiesIndeterminate,
    isCropGroupSelected,
    handleSelectedCropGroup,
    handleSelectedFarmInCropGroup,
    handleSelectedFieldInCropGroup,
    isFarmSelectedInCropGroup,
    isFieldSelectedInCropGroup,
    isPropertyIndeterminateInCropGroup,
    isCropGroupIndeterminate
  } = useMassAssignerTools({
    processedProperties,
    fieldsToMassiveAssign,
    dispatch,
    isSortByCrop,
    isSearching
  });
  const [openAddCropZonesModal, setOpenAddCropZonesModal] = useState(false);
  const scrollRef = useRef(null);

  // Sticky rows stack: Select All at the top, then each group header below
  // the headers of the groups it sits in. Rows can differ in height, so every
  // header's offset is measured from the rows actually pinned above it.
  useLayoutEffect(() => {
    const container = scrollRef.current;
    if (!container) return;
    const selectAll = container.querySelector('.mass-assigner-select-all');
    const base = selectAll?.offsetHeight ?? 0;
    const GROUP = '.cwf-kit-group-table__group';
    container.querySelectorAll('[data-group-header]').forEach(header => {
      let top = base;
      let group = header.parentElement?.parentElement?.closest(GROUP);
      while (group) {
        top += group.firstElementChild?.offsetHeight ?? 0;
        group = group.parentElement?.closest(GROUP);
      }
      // eslint-disable-next-line no-param-reassign
      header.style.top = `${top}px`;
    });
  });

  return (
    <div
      data-tree
      className="relative h-full grow flex flex-col w-full overflow-y-auto"
      data-testid="mass-assigner-tree"
    >
      <div
        ref={scrollRef}
        className="px-1 overflow-y-auto grow flex-wrap overflow-x-hidden mass-assigner-tree-scroll"
      >
        <GroupTable>
          <GroupTable.Row
            className="mass-assigner-select-all"
            onClick={handleSelectAllProperties}
            selectable
            selected={allPropertiesSelected}
            indeterminate={allPropertiesIndeterminate()}
          >
            <GroupTable.Cell>
              <div className="font-semibold text-sm">
                {isSearching
                  ? 'Select all search results'
                  : 'Select all properties'}
              </div>
            </GroupTable.Cell>
          </GroupTable.Row>
          {isSortByCrop
            ? processedProperties.map(crop => (
                <GroupByCrop
                  key={crop.id}
                  crop={crop}
                  properties={properties}
                  handleSelectedCropGroup={handleSelectedCropGroup}
                  geoJSONMap={geoJSONMap}
                  isCropGroupSelected={isCropGroupSelected}
                  handleSelectedFarmInCropGroup={handleSelectedFarmInCropGroup}
                  isFarmSelectedInCropGroup={isFarmSelectedInCropGroup}
                  handleSelectedFieldInCropGroup={
                    handleSelectedFieldInCropGroup
                  }
                  isFieldSelectedInCropGroup={isFieldSelectedInCropGroup}
                  handleSelectedCropZone={handleSelectedCropZone}
                  isCropZoneSelected={isCropZoneSelected}
                  isPropertyIndeterminate={isPropertyIndeterminate}
                  isPropertyIndeterminateInCropGroup={
                    isPropertyIndeterminateInCropGroup
                  }
                  isCropGroupIndeterminate={isCropGroupIndeterminate}
                />
              ))
            : processedProperties.map(property => (
                <GroupByFarm
                  key={property.id}
                  property={property}
                  geoJSONMap={geoJSONMap}
                  handleSelectedFarm={handleSelectedFarm}
                  isFarmSelected={isFarmSelected}
                  handleSelectedField={handleSelectedField}
                  isFieldSelected={isFieldSelected}
                  handleSelectedCropZone={handleSelectedCropZone}
                  isCropZoneSelected={isCropZoneSelected}
                  isPropertyIndeterminate={isPropertyIndeterminate}
                />
              ))}
        </GroupTable>
      </div>
      {isExportPDFMapsActive ? (
        <ExportPDFMapsFooter step={0} selectedFields={fieldsToMassiveAssign} />
      ) : (
        <MassAssignerActions
          dispatch={dispatch}
          fieldsToMassiveAssign={fieldsToMassiveAssign}
          setOpenAddCropZonesModal={setOpenAddCropZonesModal}
        />
      )}
      {openAddCropZonesModal && (
        <AddCropZonesModal
          open={openAddCropZonesModal}
          onClose={() => setOpenAddCropZonesModal(false)}
        />
      )}
    </div>
  );
};
MassAssignerTree.propTypes = {
  properties: PropTypes.arrayOf(PropTypes.object),
  fieldsAndAreasGeoJSONCollection: PropTypes.objectOf(PropTypes.any),
  propertiesFilter: PropTypes.func.isRequired,
  fieldsToMassiveAssign: PropTypes.arrayOf(PropTypes.object),
  searchTextValueFFT: PropTypes.objectOf(PropTypes.any).isRequired,
  isExportPDFMapsActive: PropTypes.bool.isRequired,
  isSortByCrop: PropTypes.bool.isRequired,
  dispatch: PropTypes.func.isRequired
};
export default memo(MassAssignerTree);
